/**
 * Compiling one block.
 *
 * Everything that can go wrong with a block — an unparseable input, a
 * missing statutory value, a compiler that emitted a malformed node — has
 * to surface as a diagnostic rather than an exception, because a document
 * is usually compiled with half its answers filled in and the host needs
 * to show progress, not a stack trace.
 */
import { validateBlockNodes } from '../ir/schema.js';
import { mapInlines } from '../ir/walk.js';
import { missingStatutorySlots } from './slots.js';
import { blockRef } from './types.js';
import { applyEditPolicy } from '../patch/lock.js';
export function compileBlock(definition, rawInput, context, options = {}) {
    const ref = blockRef(definition);
    const diagnostics = [];
    const tag = (d) => ({
        ...d,
        blockRef: ref,
        ...(options.instanceId ? { instanceId: options.instanceId } : {}),
    });
    if (definition.status !== 'published') {
        diagnostics.push(tag({
            severity: 'warning',
            code: 'block_not_published',
            message: `Block ${ref} has status "${definition.status}" — not valid for an issued document.`,
        }));
    }
    for (const slot of missingStatutorySlots(definition.slots, rawInput)) {
        diagnostics.push(tag({
            severity: slot.statutory ? 'error' : 'warning',
            code: slot.statutory ? 'missing_statutory_value' : 'missing_required_value',
            message: `${definition.title}: "${slot.label}" saknas.`,
            slotKey: slot.key,
        }));
    }
    let input = rawInput;
    if (definition.parse) {
        try {
            input = definition.parse(rawInput);
        }
        catch (error) {
            diagnostics.push(tag({
                severity: 'error',
                code: 'input_parse_failed',
                message: error instanceof Error ? error.message : String(error),
            }));
            return { nodes: [], attachments: [], diagnostics, omitted: true };
        }
    }
    let result;
    try {
        result = definition.compile(input, context);
    }
    catch (error) {
        diagnostics.push(tag({
            severity: 'error',
            code: 'compile_failed',
            message: error instanceof Error ? error.message : String(error),
        }));
        return { nodes: [], attachments: [], diagnostics, omitted: true };
    }
    if (result == null)
        return { nodes: [], attachments: [], diagnostics, omitted: true };
    const output = Array.isArray(result) ? { nodes: result } : result;
    // A blank printable form is empty by construction, not by the caller
    // remembering to withhold values: every `value` node loses its text in
    // `blank` mode and renders as a fill-in rule. One rule, applied once,
    // rather than a `cond(mode, …)` at every compiler's every value.
    const filled = context.mode === 'blank'
        ? mapInlines(output.nodes ?? [], (node) => node.type === 'value' ? { ...node, text: null, raw: null } : node)
        : output.nodes ?? [];
    // Statutory text is locked as data, so a persisted revision keeps its locks.
    const nodes = applyEditPolicy(filled, definition.editPolicy);
    diagnostics.push(...(output.diagnostics ?? []).map((d) => tag(d)));
    if (options.validate !== false) {
        const validation = validateBlockNodes(nodes);
        if (!validation.ok) {
            for (const issue of validation.issues.slice(0, 10)) {
                diagnostics.push(tag({
                    severity: 'error',
                    code: 'invalid_ir_node',
                    message: `${issue.path}: ${issue.message}`,
                }));
            }
            return { nodes: [], attachments: [], diagnostics, omitted: true };
        }
    }
    return {
        nodes,
        attachments: output.attachments ?? [],
        diagnostics,
        omitted: nodes.length === 0,
    };
}
//# sourceMappingURL=compile.js.map